import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Send, ListChecks, Loader2, RefreshCw, CheckCircle2, Activity, Archive } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/CrudModule";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { client_name: "", phone: "", email: "", state: "", product_interest: "", notes: "" };

function statusClass(status = "") {
  const s = status.toLowerCase();
  if (s.includes("issued") || s === "approved") return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (s.includes("closed")) return "bg-slate-100 text-slate-700 ring-slate-200";
  if (s.includes("underwriting") || s.includes("requirement") || s.includes("payment")) return "bg-amber-50 text-amber-800 ring-amber-200";
  if (s.includes("quoted") || s.includes("application") || s.includes("contact")) return "bg-[#315A7D]/10 text-[#2c5170] ring-[#315A7D]/25";
  return "bg-secondary text-foreground ring-border";
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="mt-2 font-heading text-2xl font-bold tabular-nums">{value ?? 0}</div>
    </div>
  );
}

export default function VendorPortal() {
  const [f, setF] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const load = () => {
    setLoading(true);
    api.get("/intake/vendor-performance")
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const submit = async () => {
    if (!f.client_name || !f.phone) { toast.error("Client name and phone are required"); return; }
    setSaving(true);
    try {
      await api.post("/intake/submit", { fields: f });
      toast.success("Lead submitted for agency review");
      setF(EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const rows = useMemo(() => {
    const all = data?.leads || [];
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [data, search]);

  const summary = data?.summary || {};
  const closed = (data?.leads || []).filter((x) => String(x.status || "").startsWith("Closed")).length;

  return (
    <div data-testid="vendor-performance-portal">
      <PageHeader
        title={data?.vendor?.name ? `${data.vendor.name} Lead Status` : "Lead Vendor Portal"}
        subtitle="Follow the progress of the business you supplied. Fortunate Operations shows operational status without exposing the agency's private records."
      >
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 mb-5">
        <Metric icon={ListChecks} label="Leads Supplied" value={summary.total} />
        <Metric icon={Activity} label="In Progress" value={summary.in_progress} />
        <Metric icon={CheckCircle2} label="Issued / Active" value={summary.issued_active} />
        <Metric icon={Archive} label="Closed" value={closed} />
      </div>

      <div className="rounded-xl border bg-card mb-6 overflow-hidden">
        <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="font-semibold">Lead Performance</div>
            <div className="text-xs text-muted-foreground">New submissions and existing-book leads linked to your vendor account.</div>
          </div>
          <Input
            className="sm:w-64"
            placeholder="Search client or status…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {loading ? (
          <div className="grid place-items-center h-40 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">No linked leads yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary/50 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Product / State</th>
                  <th className="px-4 py-3">Current Status</th>
                  <th className="px-4 py-3">Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.source}-${r.source_reference || i}`} className="border-t">
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.client_name}</div>
                      <div className="font-mono text-[10px] text-muted-foreground">{r.source_reference || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">{r.source}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {[r.product, r.state].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset ${statusClass(r.status)}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="max-w-2xl rounded-xl border bg-card p-5">
        <div className="mb-1 flex items-center gap-2 font-semibold"><Send className="h-4 w-4" /> Submit a New Lead</div>
        <p className="mb-4 text-xs text-muted-foreground">The agency reviews every submission before it enters the active pipeline.</p>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div><Label className="text-xs">Client Name *</Label><Input value={f.client_name} onChange={(e) => set("client_name", e.target.value)} data-testid="v-name" /></div>
            <div><Label className="text-xs">Phone *</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="v-phone" /></div>
            <div><Label className="text-xs">Email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
            <div><Label className="text-xs">State</Label><Input value={f.state} onChange={(e) => set("state", e.target.value)} /></div>
          </div>
          <div><Label className="text-xs">Product Interest</Label><Input value={f.product_interest} onChange={(e) => set("product_interest", e.target.value)} /></div>
          <div><Label className="text-xs">Notes</Label><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Do not include SSN, banking, card or medical details." /></div>
          <p className="text-[11px] text-muted-foreground">Do not submit Social Security numbers, banking/payment-card data, passwords, or medical details here.</p>
          <Button onClick={submit} disabled={saving} data-testid="v-submit">
            {saving ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Submitting…</> : "Submit for Review"}
          </Button>
        </div>
      </div>
    </div>
  );
}
